import { html, nothing } from "lit";
import type { PresenceEntry } from "../types.ts";
import {
  formatPresenceAge,
  formatPresenceLastActivity,
  formatPresenceSummary,
} from "../presenter.ts";

/** Derive a human-readable connectivity status from lastInputSeconds. */
function deriveStatus(entry: PresenceEntry): "online" | "idle" | "offline" | "unknown" {
  const secs = entry.lastInputSeconds ?? null;
  if (secs === null) return "unknown";
  if (secs < 60) return "online";
  if (secs < 300) return "idle";
  return "offline";
}

function statusChipClass(status: string): string {
  if (status === "online") return "chip chip-ok";
  if (status === "idle") return "chip chip-warn";
  if (status === "offline") return "chip chip-danger";
  return "chip";
}

function statusDotClass(status: string): string {
  if (status === "online") return "status-dot status-dot--online";
  if (status === "idle") return "status-dot status-dot--idle";
  if (status === "offline") return "status-dot status-dot--offline";
  return "status-dot status-dot--unknown";
}

export type InstancesProps = NodesProps & {
  loading: boolean;
  entries: PresenceEntry[];
  lastError: string | null;
  statusMessage: string | null;
  onRefresh: () => void;
};

import { renderNodesContent, type NodesProps } from "./nodes.ts";

export function renderInstances(props: InstancesProps) {
  return html`
    <section class="card">
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">Connected Instances</div>
          <div class="card-sub">Presence beacons from the gateway and clients.</div>
        </div>
        <button class="btn" ?disabled=${props.loading} @click=${props.onRefresh}>
          ${props.loading ? "Loading\u2026" : "Refresh"}
        </button>
      </div>
      ${
        props.lastError
          ? html`<div class="callout danger" style="margin-top: 12px;">
            ${props.lastError}
          </div>`
          : nothing
      }
      ${
        props.statusMessage
          ? html`<div class="callout" style="margin-top: 12px;">
            ${props.statusMessage}
          </div>`
          : nothing
      }
      <div class="list" style="margin-top: 16px;">
        ${
          props.entries.length === 0
            ? html`
                <div class="muted">No instances reported yet.</div>
              `
            : props.entries.map((entry) => renderEntry(entry))
        }
      </div>
    </section>
    ${renderNodesContent(props)}
  `;
}

function renderEntry(entry: PresenceEntry) {
  const mode = entry.mode ?? "unknown";
  const roles = Array.isArray(entry.roles) ? entry.roles.filter(Boolean) : [];
  const scopes = Array.isArray(entry.scopes) ? entry.scopes.filter(Boolean) : [];
  const scopesLabel =
    scopes.length > 0
      ? scopes.length > 3
        ? `${scopes.length} scopes`
        : `scopes: ${scopes.join(", ")}`
      : null;
  const status = deriveStatus(entry);
  const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);
  return html`
    <div class="list-item">
      <div class="list-main">
        <div class="list-title">
          <span class=${statusDotClass(status)} aria-hidden="true"></span>
          ${entry.host ?? "unknown host"}
        </div>
        <div class="list-sub">${formatPresenceSummary(entry)}</div>
        <div class="chip-row">
          <span class=${statusChipClass(status)}>${statusLabel}</span>
          <span class="chip">${mode}</span>
          ${roles.map((role) => html`<span class="chip">${role}</span>`)}
          ${scopesLabel ? html`<span class="chip">${scopesLabel}</span>` : nothing}
          ${entry.platform ? html`<span class="chip">${entry.platform}</span>` : nothing}
          ${entry.deviceFamily ? html`<span class="chip">${entry.deviceFamily}</span>` : nothing}
          ${
            entry.modelIdentifier
              ? html`<span class="chip">${entry.modelIdentifier}</span>`
              : nothing
          }
          ${entry.version ? html`<span class="chip">${entry.version}</span>` : nothing}
        </div>
      </div>
      <div class="list-meta">
        <div>${formatPresenceAge(entry)}</div>
        <div class="muted">Last activity ${formatPresenceLastActivity(entry)}</div>
        <div class="muted">Reason ${entry.reason ?? ""}</div>
      </div>
    </div>
  `;
}
